import { showHttpError } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'

import { rootApi } from './root-api'

const api = rootApi.enhanceEndpoints({ addTagTypes: [
  'CURRENT_USER',
  'CURRENT_ORGANIZATION',
  'SEND_OPTIONS',
  'EVENT_TYPES_COLLECTION'] }).injectEndpoints({
  endpoints: (build) => ({
    getUser: build.query({
      query: () => ({ url: '/api/user' }),
      providesTags: ['CURRENT_USER'],
      extraOptions: {
        transformResponse: false
      }
    }),
    getOrganization: build.query({
      query: () => ({ url: '/api/organization' }),
      providesTags: ['CURRENT_ORGANIZATION'],
      extraOptions: {
        transformResponse: false
      }
    }),
    getPolicy: build.query({
      query: () => ({ url: '/api/policy' })
    }),
    getSendOptions: build.query({
      query: ({ ownerType, ownerId }) => ({
        url: ownerType === 'organization'
          ? `/api/send_from_calendly/${ownerType}/emails`
          : `/api/send_from_calendly/${ownerType}/${ownerId}/emails`,
      }),
      providesTags: (result) => (
        result.sendOptions
          ? [...result.sendOptions.map((account) => (
            { type: 'SEND_OPTIONS', id: account.account_uuid }
          )
          )] : ['SEND_OPTIONS']
      )
    }),
    getEventTypesCollection: build.query({
      query: ({ filterTerm, scope, filterParams, skipPagination, page }) => ({
        url: '/api/users/me/event_types',
        params: {
          ...(scope ? { scope } : {}),
          filter_term: filterTerm,
          skip_pagination: skipPagination,
          ...(filterParams || {}),
          page
        }
      }),
      providesTags: ['EVENT_TYPES_COLLECTION'],
      extraOptions: {
        transformResponse: false
      }
    }),
    saveUserFeatureIntent: build.mutation({
      query: (featureKeys = []) => ({
        url: '/api/user_feature_intent',
        method: 'PUT',
        data: { feature_keys: featureKeys }
      }),
      invalidatesTags: ['CURRENT_USER'],
      onQueryStarted: async (featureKeys, { dispatch, queryFulfilled }) => {
        const optimisticUpdate = dispatch(
          api.util.updateQueryData('getUser', undefined, (draft) => {
            if (!draft) return
            draft.user_feature_intent = { feature_keys: featureKeys }
          })
        )

        try {
          await queryFulfilled
        } catch (e) {
          optimisticUpdate.undo()
        }
      }
    }),
    getPaidBookingStatus: build.query({
      query: (id = 'me') => ({ url: `/api/users/${id}/paid_booking_status` }),
      extraOptions: {
        transformResponse: false
      }
    })
  })
})

export const {
  useGetUserQuery,
  useGetOrganizationQuery,
  useGetPolicyQuery,
  useGetSendOptionsQuery,
  useLazyGetSendOptionsQuery,
  useGetEventTypesCollectionQuery,
  useSaveUserFeatureIntentMutation,
  useGetPaidBookingStatusQuery
} = api
export const { getUser, getOrganization, getPolicy } = api.endpoints

/** Start core session queries before the app tree mounts so the shell gate resolves sooner. */
export const prefetchCoreAppData = (dispatch) => {
  dispatch(getUser.initiate())
  dispatch(getOrganization.initiate())
  dispatch(getPolicy.initiate())
}
export const setUser = (user) => api.util.updateQueryData('getUser', undefined, (state) => { Object.assign(state, user) })
export const setOrganization = (org) => api.util.updateQueryData('getOrganization', undefined, (state) => { Object.assign(state, org) })

const mergeProfileSaveIntoUserDraft = (draft, response, formValues) => {
  if (!draft) {
    return
  }

  if (formValues) {
    if (typeof formValues.name === 'string') {
      draft.name = formValues.name
    }
    if (formValues.language != null) {
      draft.locale = formValues.language
    }
    if (formValues.country != null) {
      draft.country_code = formValues.country
    }
    if (formValues.dateNotation != null) {
      draft.date_notation = formValues.dateNotation
    }
    if (formValues.timeNotation != null) {
      draft.time_notation = formValues.timeNotation
    }

    if (!draft.profile) {
      draft.profile = {}
    }
    if (formValues.timezone != null) {
      draft.profile.timezone = formValues.timezone
    }
    if (formValues.description !== undefined) {
      draft.profile.description = formValues.description
    }

    if (formValues.avatar?.remove) {
      draft.avatar_url = null
    }
  }

  if (response && typeof response === 'object') {
    if (response.name != null) draft.name = response.name
    if (response.avatar_url !== undefined) draft.avatar_url = response.avatar_url
    if (response.user_locale != null) draft.locale = response.user_locale
    if (response.user_country_code != null) draft.country_code = response.user_country_code
    if (response.user_date_notation != null) draft.date_notation = response.user_date_notation
    if (response.user_time_notation != null) draft.time_notation = response.user_time_notation
    if (response.profile && typeof response.profile === 'object') {
      if (!draft.profile) {
        draft.profile = {}
      }
      Object.assign(draft.profile, response.profile)
    }
  }
}

/**
 * Merges a profile form save into the RTK `getUser` cache for immediate UI updates.
 * Callers should still invalidate `CURRENT_USER` so server-computed fields refetch.
 */
export const patchCurrentUserAfterProfileSave = (response, formValues) => (
  api.util.updateQueryData('getUser', undefined, (draft) => {
    mergeProfileSaveIntoUserDraft(draft, response, formValues)
  })
)

const processNewActivationData = ({ getState, getNewUserActivationData }) => {
  const { activation_data: activationData } = getUser.select()(getState())?.data || {}
  if (typeof getNewUserActivationData === 'function') {
    return getNewUserActivationData(activationData)
  }
  return { ...activationData, ...getNewUserActivationData }
}

api.injectEndpoints({
  endpoints: (build) => ({
    updateUserActivationData: build.mutation({
      query: (getNewUserActivationData) => ({ getState }) => {
        const { id } = getUser.select()(getState())?.data || {}

        return ({
          url: `/api/users/${id}/activation_data`,
          method: 'patch',
          data: {
            activation_data: processNewActivationData({ getState, getNewUserActivationData }),
          },
        })
      },
      extraOptions: {
        handleErrors: false,
      },
      onQueryStarted: async (getNewUserActivationData, { dispatch, getState }) => {
        dispatch(setUser({ activation_data: processNewActivationData({ getState, getNewUserActivationData }) }))
      }
    }),
    updateUserGrowthExperiments: build.mutation({
      query: (growthExperimentsData) => ({ getState }) => {
        const { id } = getUser.select()(getState())?.data || {}

        return ({
          url: `/api/users/${id}/growth_experiments`,
          method: 'patch',
          data: {
            growth_experiments: growthExperimentsData,
          },
        })
      },
      extraOptions: {
        handleErrors: false,
      },
      onQueryStarted: async (growthExperimentsData, { dispatch, queryFulfilled }) => {
        const optimisticUpdate = dispatch(
          api.util.updateQueryData('getUser', undefined, (draft) => {
            const existingGrowthExperiments = draft.growth_experiments || {}
            draft.growth_experiments = { ...existingGrowthExperiments, ...growthExperimentsData }
          })
        )

        try {
          await queryFulfilled
        } catch (e) {
          optimisticUpdate.undo()
          dispatch(showHttpError(e.error))
        }
      }
    }),
    startNotetakerTrial: build.mutation({
      query: ({ notetakerScope, recapEmailRecipients }) => ({
        url: '/api/internal/notetaker_trial',
        method: 'post',
        data: {
          notetaker_scope: notetakerScope,
          recap_email_recipients: recapEmailRecipients,
        },
      }),
      extraOptions: {
        handleErrors: true,
      },
    }),
    enableNotetakerMetered: build.mutation({
      query: () => ({
        url: '/api/internal/notetaker_metered_enable',
        method: 'post',
      }),
      extraOptions: {
        handleErrors: true,
      },
    })
  })
})
export const {
  useUpdateUserActivationDataMutation,
  useUpdateUserGrowthExperimentsMutation,
  useStartNotetakerTrialMutation,
  useEnableNotetakerMeteredMutation
} = api

export const getAppSettings = () => httpRequest({
  url: '/api/app/settings',
  method: 'GET'
})

export const getBootstrapData = () => httpRequest({
  url: '/api/app/publisher/bootstrap',
  method: 'GET'
})

// Hooks

export const useGetUserProfile = () => {
  const { data: { profile } = {} } = useGetUserQuery()

  return profile
}

export const useGetOrganizationUuid = () => {
  const { data: organizationData } = useGetOrganizationQuery()

  return organizationData && organizationData.uri.split('/').pop()
}
